import {ManagingResources} from "../common/lifetime";
import {autoRegister, resolve} from "../container";
import {prepareEvents} from "../common/utils/events";
import {GLOBAL} from "../common/globals";
import {Tracking} from "./tracking";
import {Nav} from "../common/nav";
import {TrackingContext} from "./trackingContext";
import {
    type GenericTrackingEvent,
    type GenericTrackingEventPayload,
    type LinkInfo,
    type NavigationTrackingEvent,
    type NavigationTrackingEventPayload,
    type TrackingEvent,
    TrackingEventLocation,
    TrackingEventName,
    TrackingEventType
} from "./types";
import {omitUndefined} from "../common/utils/objects";
import type {Maybe, Nullable} from "../bootstrap/common/types";
import {TrackingEventFactories} from "./trackingEventFactory";

@autoRegister()
export class ClickTracking {

    public constructor(
        private nav: Nav = resolve(Nav),
        private tracking: Tracking = resolve(Tracking),
        private trackingEventFactory: TrackingEventFactories = resolve(TrackingEventFactories)
    ) {
    }

    public handleTextlinkInteract(event: Event, text: string, link?: string): void {
        const textlinkEvent: GenericTrackingEvent = {
            name: TrackingEventName.TEXTLINK_INTERACT,
            data: {
                text: text,
                contentId: TrackingContext.makeContentId(event),
                type: TrackingEventType.CLICK
            }
        };
        if (link) {
            textlinkEvent.data.link = this.linkInfo(link);
        }
        this.tracking.emitTrackingEvent(textlinkEvent);
    }

    public handleButtonInteract(event: Event, text: string): void {
        this.tracking.emitTrackingEvent({
            name: TrackingEventName.BUTTON_INTERACT,
            data: {
                text: text,
                contentId: TrackingContext.makeContentId(event),
                type: TrackingEventType.CLICK
            }
        });
    }

    public handleNavigationInteract(event: NavigationTrackingEvent): void {
        this.tracking.emitNavigationTrackingEvent(event);
    }

    public handleBodyClick(event: MouseEvent): void {
        const context = TrackingContext.fromEvent(event);
        if (!context) {
            return;
        }
        const trackable = this.trackingEventFromAttributes(event);
        if (trackable) {
            if (trackable.name === TrackingEventName.NAVIGATION_INTERACT) {
                return this.tracking.emitNavigationTrackingEvent({
                    name: trackable.name,
                    data: trackable.data as NavigationTrackingEventPayload
                });
            }
            return this.tracking.emitTrackingEvent({
                name: trackable.name,
                data: trackable.data as GenericTrackingEventPayload
            });
        }
        const eventName = this.getEventName(event);
        if (eventName === TrackingEventName.NAVIGATION_INTERACT) {
            return this.tracking.emitNavigationTrackingEvent({
                name: eventName,
                data: omitUndefined({
                    text: context.getEventLabel(),
                    contentId: TrackingContext.makeContentId(event),
                    type: TrackingEventType.CLICK,
                    location: this.locationOf(event)
                })
            });
        }
        return this.tracking.emitTrackingEvent({
            name: eventName,
            data: omitUndefined({
                text: context.getEventLabel(),
                contentId: TrackingContext.makeContentId(event),
                type: TrackingEventType.CLICK,
                link: this.linkInfo(context.clickedElement.getAttribute("href"))
            })
        });
    }

    private locationOf(event: Event): TrackingEventLocation {
        const eventPath: string[] = Array.from(event.composedPath())
            .filter(element => element instanceof Element)
            .map(element => element.tagName);
        if (eventPath.includes("HEADER")) {
            return TrackingEventLocation.HEADER;
        }
        if (eventPath.includes("FOOTER")) {
            return TrackingEventLocation.FOOTER;
        }
        return TrackingEventLocation.MAIN;
    }

    private linkInfo(targetUrl: Nullable<string>): Maybe<LinkInfo> {
        if (!targetUrl) {
            return undefined;
        }
        return {
            targetUrl: this.nav.absoluteHrefFrom(targetUrl),
            isExternal: !this.nav.isInternalLink(targetUrl)
        };
    }

    private getEventName(event: MouseEvent): TrackingEventName {
        if (this.isNavigationElement(event)) {
            return TrackingEventName.NAVIGATION_INTERACT;
        }
        return TrackingEventName.TEXTLINK_INTERACT;
    }

    private trackingEventFromAttributes(event: Event): Nullable<TrackingEvent<any>> {
        return this.trackingEventFactory.createFromAttributes(event);
    }

    private isNavigationElement(event: MouseEvent): boolean {
        const composedPath: string[] = Array.from(event.composedPath())
            .filter(element => element instanceof Element)
            .map(element => element.tagName);

        return composedPath.includes("HEADER")
            || composedPath.includes("FOOTER")
            || composedPath.includes("EOP-CHAPTER-NAVIGATION")
            || composedPath.includes("EOP-CONTENT-NAVIGATION");
    }
}

export class EopClickTracking extends ManagingResources(HTMLElement) {

    public constructor(private clickTrackingListener: ClickTracking = resolve(ClickTracking)) {
        super();
    }

    public connectedCallback(): void {
        prepareEvents(GLOBAL.bodyElement())
            .boundTo(this)
            .on("click", ev => this.clickTrackingListener.handleBodyClick(ev as MouseEvent));
    }
}

customElements.define("eop-click-tracking", EopClickTracking);